/**
 * IncompleteIncidentsWarning - Modal warning for users with too many incomplete incidents
 * Shows on login/page change if user has 5+ incomplete incidents
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../api/client';

const MAX_INCOMPLETE_INCIDENTS = 5;

export default function IncompleteIncidentsWarning() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  
  const [open, setOpen] = useState(false);
  const [incompleteIncidents, setIncompleteIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  // Fetch incomplete incidents for this user
  const checkIncompleteIncidents = useCallback(async () => {
    if (!user?.rangerId) return;
    
    setLoading(true);
    try {
      const res = await api.get('/incidents', { 
        params: { 
          status: 'incomplete',
          pageSize: 50,
        } 
      });
      
      const items = res.data?.items || [];
      // Filter to only this user's incomplete incidents with status = incomplete
      const myIncomplete = items.filter(inc => 
        inc.status?.toLowerCase() === 'incomplete' &&
        inc.primaryRangerId?.toUpperCase() === user.rangerId.toUpperCase()
      );
      
      setIncompleteIncidents(myIncomplete);
      
      // Show warning if at or over limit
      if (myIncomplete.length >= MAX_INCOMPLETE_INCIDENTS) {
        setOpen(true);
      }
    } catch (e) {
      console.error('Failed to check incomplete incidents:', e);
    }
    setLoading(false);
    setHasChecked(true);
  }, [user?.rangerId]);

  // Check on initial load and page changes
  useEffect(() => {
    // Small delay to not block initial render
    const timer = setTimeout(() => {
      checkIncompleteIncidents();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [location.pathname, checkIncompleteIncidents]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleGoToIncidents = () => {
    setOpen(false);
    navigate('/dashboard');
  };

  if (!user?.rangerId) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '3px solid',
          borderColor: 'warning.main',
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'warning.main', 
        color: 'warning.contrastText',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}>
        <WarningAmberIcon sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h6" fontWeight={800}>
            INCOMPLETE INCIDENTS WARNING
          </Typography>
          <Typography variant="body2">
            You have {incompleteIncidents.length} incident{incompleteIncidents.length !== 1 ? 's' : ''} missing information
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ 
          bgcolor: 'error.dark', 
          color: 'white', 
          p: 2, 
          borderRadius: 2, 
          mb: 3,
        }}>
          <Typography variant="body1" fontWeight={700} sx={{ mb: 1 }}>
            ⚠️ ACTION REQUIRED
          </Typography>
          <Typography variant="body2">
            You have reached the maximum of {MAX_INCOMPLETE_INCIDENTS} incomplete incidents. 
            You must complete these incidents before using "Finish Later" on new incidents.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5, fontStyle: 'italic' }}>
            Failure to maintain accurate notes and disposition information may result in 
            removal of the ability to finish incidents later.
          </Typography>
        </Box>

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.secondary' }}>
          YOUR INCOMPLETE INCIDENTS:
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List dense sx={{ bgcolor: 'background.default', borderRadius: 2 }}>
            {incompleteIncidents.slice(0, 10).map((inc, idx) => (
              <ListItem 
                key={inc.id || idx}
                sx={{ 
                  borderBottom: idx < incompleteIncidents.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={700}>
                        {inc.incidentType?.replace(/-/g, ' ').toUpperCase() || 'Unknown Type'}
                      </Typography>
                      <Chip 
                        label="INCOMPLETE" 
                        size="small" 
                        color="warning" 
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(inc.occurredAtUtc)} • {inc.source || 'No source'}
                      {!inc.notes && ' • Missing notes'}
                      {(!inc.servicesProvided || inc.servicesProvided.length === 0) && ' • Missing disposition'}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={handleClose} color="inherit">
          Remind Me Later
        </Button>
        <Button 
          variant="contained" 
          color="warning" 
          onClick={handleGoToIncidents}
          sx={{ fontWeight: 700 }}
        >
          Go to Dashboard
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Export the max for use in other components
export { MAX_INCOMPLETE_INCIDENTS };
